import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { mapLinks, useGetLinks } from "@insite/client-framework/Store/Links/LinksSelectors";
import { WidgetDefinition } from "@insite/client-framework/Types/ContentItemDefinitions";
import { HasFields } from "@insite/client-framework/Types/ContentItemModel";
import { LinkFieldValue } from "@insite/client-framework/Types/FieldDefinition";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { useMergeStyles } from "@insite/content-library/additionalStyles";
import Rss from "@insite/mobius/Icons/Rss";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import Facebook from "@insite/mobius/Icons/Facebook";
import Linkedin from "@insite/mobius/Icons/Linkedin";
import Twitter from "@insite/mobius/Icons/Twitter";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import { breakpointMediaQueries } from "@insite/mobius/utilities";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import * as React from "react";
import { FC } from "react";
import { connect } from "react-redux";
import { css } from "styled-components";

const enum fields {
    links = "links",
    visibilityState = "visibilityState",
}

const enum icons {
    facebook = "Facebook",
    twitter = "Twitter",
    linkedin = "Linkedin",
}

const enum visibilityState {
    both = "both",
    label = "label",
    icon = "icon",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.visibilityState]: visibilityState;
        [fields.links]: LinkModel[];
    };
}

interface LinkModel {
    fields: {
        title: string;
        icon: string;
        openInNewWindow: boolean;
        destination: LinkFieldValue;
        overriddenTitle: string;
    };
}

export interface LinkListStyles {
    socialLinkListWrapper?: InjectableCss;
    linkWrapper?: InjectableCss;
    link?: LinkPresentationProps;
}

export const linkListStyles: LinkListStyles = {
    socialLinkListWrapper: {
        css: css`
            width: 100%;
            padding: 5px;
            display: flex;
            flex-wrap: wrap;
            align-items: flex-start;
            border-radius: 50%;
            width: 36px;
            height: 36px;
            background: linear-gradient(268deg, #0b70b7 0%, #44a8f7 100%);
            & :hover {
                background: #0b70b7;
            }
        `,
    },
    linkWrapper: {
        css: css`
            flex-shrink: 0;
            a {
                span {
                    svg {
                        color: #fff;
                        fill: #fff;
                        width: 14px;
                        height: 14px;
                        @media only screen and (min-device-width: 768px) and (max-device-width: 1024px) and (orientation: landscape) {
                            width: 21px;
                            height: 21px;
                        }
                        ${({ theme }: { theme: BaseTheme }) =>
                            breakpointMediaQueries(
                                theme,
                                [
                                    css`
                                        width: 21px;
                                        height: 21px;
                                    `,
                                ],
                                "max",
                            )}
                    }
                }
            }
        `,
    },
    link: {
        typographyProps: {
            // color: "text.main",
        },
    },
};

interface OptionalFieldReturn {
    title: string;
    icon: string;
    openInNewWindow: boolean;
}

const mapStateToProps = (state: ApplicationState, ownProps: OwnProps) => {
    const links = mapLinks<LinkModel, OptionalFieldReturn>(state, ownProps.fields.links, (widgetLink, stateLink) => ({
        title: widgetLink.fields.title ?? stateLink?.title,
        icon: widgetLink.fields.icon,
        openInNewWindow: widgetLink.fields.openInNewWindow,
    }));
    return {
        links,
    };
};

type Props = OwnProps & ReturnType<typeof mapStateToProps>;

export const TeamIconStyles = linkListStyles;

const GetIcon = (icon: string) => {
    switch (icon) {
        case icons.twitter:
            return Twitter;
        case icons.facebook:
            return Facebook;
        case icons.linkedin:
            return Linkedin;

        default:
            break;
    }

    return "";
};
	
const TeamIcon: FC<OwnProps> = ({ fields }) => {

    const links = useGetLinks(fields.links, o => o.fields.destination);
    const styles = useMergeStyles("socialLinks", linkListStyles);

    const TeamIconStyle = css`
        ${styles.socialLinkListWrapper?.css}
        ${styles.linkWrapper?.css}
    `;
    const showIcon = fields.visibilityState === visibilityState.both || fields.visibilityState === visibilityState.icon;
    const showLabel =
        fields.visibilityState === visibilityState.both || fields.visibilityState === visibilityState.label;

    return (
        <StyledWrapper css={TeamIconStyle}>
            {links.map((link, index) => (
                // eslint-disable-next-line react/no-array-index-key
                <StyledWrapper key={index}>
                    {link.url && (
                        <Link
                            {...styles.link}
                            href={link.url}
                            target={fields.links[index].fields.openInNewWindow ? "_blank" : ""}
                            icon={{
                                iconProps: {
                                    css: css`
                                        margin: 5px;
                                        width: 18px;
                                        height: 18px;
                                    `,
                                    src: showIcon ? GetIcon(fields.links[index].fields.icon) : undefined,
                                    title: fields.links[index].fields.title,
                                },
                            }}
                        >
                            {showLabel && fields.links[index].fields.title}
                        </Link>
                    )}
                </StyledWrapper>
            ))}
        </StyledWrapper>
    );
};
const contentTab = {
    displayName: "Content",
    sortOrder: 1,
};
const settingsTab = {
    displayName: "Settings",
    sortOrder: 2,
};

const definition: WidgetDefinition = {
    group: "Basic",
    icon: "thumbs-up",
    isSystem: true,
    fieldDefinitions: [
        {
            name: fields.links,
            editorTemplate: "ListField",
            tab: contentTab,
            // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
            getDisplay: (item: HasFields) => {
                return item.fields.title;
            },
            defaultValue: [],
            fieldType: "Translatable",
            fieldDefinitions: [
                {
                    name: "icon",
                    displayName: "Select Icon",
                    editorTemplate: "DropDownField",
                    options: [
                        { displayName: "Facebook", value: icons.facebook },
                        { displayName: "Twitter", value: icons.twitter },
                        { displayName: "Linkedin", value: icons.linkedin },
                    ],
                    defaultValue: "",
                },
                {
                    name: "destination",
                    editorTemplate: "LinkField",
                    defaultValue: {
                        value: "",
                        type: "Url",
                    },
                    isRequired: true,
                },
                {
                    name: "openInNewWindow",
                    editorTemplate: "CheckboxField",
                    defaultValue: false,
                },
            ],
        },

        {
            name: fields.visibilityState,
            displayName: "Visibility",
            editorTemplate: "RadioButtonsField",
            tab: settingsTab,
            options: [
                {
                    displayName: "Show Icon Only",
                    value: visibilityState.icon,
                },
                {
                    displayName: "Show Both",
                    value: visibilityState.both,
                },
                {
                    displayName: "Show Label Only",
                    value: visibilityState.label,
                },
            ],
            defaultValue: visibilityState.icon,
            fieldType: "Translatable",
            sortOrder: 4,
        },
    ],
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps)(TeamIcon),
    definition,
};

export default widgetModule;
